import { Button, Close } from 'components/Buttons'
import { Modal } from 'components/Modal'
import { LoadingCircle } from 'components/Loading'
import { Pagination } from 'components/Pagination'
import { SearchBar } from 'components/Search'
import { CheckIcon } from '@heroicons/react/solid'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import config from 'config'
import { Role, StaffMember } from 'models'
import { QueryFilter, ReadResult } from 'interfaces/queryOptions'
import { list as listStaff, listRoles } from 'modules/Staff/services/api'
import { updateOrderOwner } from '../services/update'

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  orderId: number
  hasOwner: boolean
  onAssigned: (staffMember: ReadResult<StaffMember>) => void
}

export function AssignOwnerModal({
  open,
  setOpen,
  orderId,
  hasOwner,
  onAssigned
}: Props) {
  const [selectedStaff, setSelectedStaff] =
    useState<ReadResult<StaffMember> | null>(null)
  const [data, setData] = useState<ReadResult<StaffMember>[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [search, setSearch] = useState('')
  const [onlySales, setOnlySales] = useState(true)
  const [salesRoleId, setSalesRoleId] = useState<number | null>(null)

  const maxItemsPerPage = config.items_per_list_page

  useEffect(() => {
    if (open) {
      setSelectedStaff(null)
      setSearch('')
      setPage(1)
      setOnlySales(true)
    }
  }, [open])

  // Resolve o id da role SALES uma única vez, para usar no filtro
  useEffect(() => {
    if (!open || salesRoleId != null) return

    async function loadSalesRoleId() {
      try {
        const { data } = await listRoles()
        const salesRole = data.find(
          (role) => role.attributes.name === Role.SALES
        )
        if (salesRole) setSalesRoleId(salesRole.id)
      } catch {
        toast.error('Falha ao buscar roles')
      }
    }
    loadSalesRoleId()
  }, [open, salesRoleId])

  // Limpa a seleção sempre que a lista muda (busca, página ou filtro), já
  // que o staff selecionado pode não estar mais visível
  useEffect(() => {
    setSelectedStaff(null)
  }, [search, page, onlySales])

  useEffect(() => {
    if (!open) return
    // Enquanto o filtro "somente Sales" estiver marcado, aguarda resolver o
    // id da role antes de buscar, para não mostrar a lista sem filtro por um instante
    if (onlySales && salesRoleId == null) return

    // Evita que uma resposta obsoleta (ex: do fetch anterior a um
    // fechar/reabrir rápido do modal) sobrescreva um resultado mais recente
    let active = true

    async function loadData() {
      setIsLoading(true)
      try {
        const filters: QueryFilter[] =
          onlySales && salesRoleId != null
            ? [{ key: 'role_id', op: 'eq', value: salesRoleId }]
            : []
        const options = {
          attributes: ['id', 'fullname', 'email'],
          limit: maxItemsPerPage,
          search,
          page,
          sort: { fullname: 1 },
          filters
        }
        const { count, data } = await listStaff(options)
        if (!active) return
        setData(data as ReadResult<StaffMember>[])
        setTotal(count)
      } catch {
        if (active) toast.error('Falha ao buscar staff members')
      } finally {
        if (active) setIsLoading(false)
      }
    }
    loadData()

    return () => {
      active = false
    }
  }, [open, maxItemsPerPage, page, search, onlySales, salesRoleId])

  useEffect(() => {
    setLastPage(Math.ceil(total / maxItemsPerPage))
  }, [total, maxItemsPerPage])

  const onSearch = useCallback((term: string) => {
    setSearch(term)
    setPage(1)
  }, [])

  const onToggleOnlySales = useCallback(() => {
    setOnlySales((prev) => !prev)
    setPage(1)
  }, [])

  const onPageChange = useCallback(
    (p: number) => {
      setPage((prev) => Math.min(Math.max(1, p), lastPage || prev))
    },
    [lastPage]
  )

  const onSave = useCallback(async () => {
    if (selectedStaff == null) return
    setIsSaving(true)
    try {
      await updateOrderOwner(orderId, selectedStaff.id.toString())
      onAssigned(selectedStaff)
      toast.success('Responsável atualizado com sucesso')
      setOpen(false)
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Não foi possível atualizar o responsável'
      )
    } finally {
      setIsSaving(false)
    }
  }, [orderId, selectedStaff, onAssigned, setOpen])

  return (
    <Modal
      open={open}
      title={hasOwner ? 'Alterar responsável' : 'Atribuir responsável'}
      onClose={() => setOpen(false)}
      width="sm:w-1/2"
    >
      <div className="mt-4 space-y-5">
        <SearchBar value={search} onChange={onSearch} />
        <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
          <input
            type="checkbox"
            className="sr-only"
            checked={onlySales}
            onChange={onToggleOnlySales}
          />
          <div
            className={`${
              onlySales
                ? 'bg-emerald-600 border-transparent'
                : 'bg-white border-gray-300'
            } h-4 w-4 border rounded flex items-center justify-center`}
          >
            {onlySales && <CheckIcon className="w-3 h-3 text-white" />}
          </div>
          <span className="text-sm text-gray-700">
            Filtrar por vendedores (SALES)
          </span>
        </label>
        {isLoading ? (
          <LoadingCircle />
        ) : (
          <div className="flex flex-col animate-fade-in-down">
            <div className="-my-2 max-h-64 overflow-y-auto overflow-x-auto px-4">
              <div className="py-2 align-middle inline-block min-w-full">
                <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-center text-sm font-light text-gray-500 w-12" />
                        <th className="px-4 py-3 text-left text-sm font-light text-gray-500">
                          NOME
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-light text-gray-500">
                          EMAIL
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.map((staff, index) => (
                        <tr
                          key={staff.id}
                          className={
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }
                        >
                          <td className="px-4 py-3 text-center text-sm whitespace-nowrap w-12">
                            <input
                              type="radio"
                              name="owner"
                              checked={selectedStaff?.id === staff.id}
                              onChange={() => setSelectedStaff(staff)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap max-w-xxs">
                            <div className="text-xs font-semibold text-gray-900 truncate">
                              {staff.attributes.fullname ?? '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap max-w-xxs">
                            <div className="text-xs text-gray-500 truncate">
                              {staff.attributes.email ?? '-'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="mx-4">
              <Pagination
                currentPage={page}
                pageSize={maxItemsPerPage}
                itemsOnPage={data.length}
                totalItems={total}
                onPageChange={onPageChange}
              />
            </div>
          </div>
        )}
      </div>
      <div className="mt-6 flex justify-end space-x-4">
        <Close onClick={() => setOpen(false)} />
        <Button
          disabled={isSaving || selectedStaff == null}
          loading={isSaving}
          type="button"
          onClick={onSave}
        />
      </div>
    </Modal>
  )
}
